import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import type { SessionUser } from "./api";

/** Which layout a visitor gets. A guest is the absence of a session, not a role. */
export type Layout = "guest" | "starter" | "pro" | "admin";

export function layoutOf(user: SessionUser | null | undefined): Layout {
  return user ? user.role : "guest";
}

const ME = ["me"] as const;

export function useSession() {
  const query = useQuery({
    queryKey: ME,
    queryFn: () => api<{ user: SessionUser | null }>("/me").then((r) => r.user),
    staleTime: 60_000,
  });
  const user = query.data ?? null;
  return { user, layout: layoutOf(user), isLoading: query.isLoading };
}

/**
 * Every way the session changes, as mutations that write the new user straight
 * into the cache — and drop every other query, since what a page shows depends
 * on who is looking.
 */
export function useAuthActions() {
  const qc = useQueryClient();
  const settle = (user: SessionUser | null) => {
    if (user === null) {
      // Signed out: member-only data is dropped, not refetched — a refetch
      // here would ask for the Dashboard as a guest and get a 401 for its trouble.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      qc.setQueryData(ME, null);
      return;
    }
    qc.setQueryData(ME, user);
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "me" });
  };

  const demo = useMutation({
    mutationFn: (persona: string) =>
      api<{ user: SessionUser }>("/auth/demo", { method: "POST", body: { persona } }),
    onSuccess: (r) => settle(r.user),
  });
  const login = useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api<{ user: SessionUser }>("/auth/login", { method: "POST", body }),
    onSuccess: (r) => settle(r.user),
  });
  const signup = useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api<{ user: SessionUser }>("/auth/signup", { method: "POST", body }),
    onSuccess: (r) => settle(r.user),
  });
  const logout = useMutation({
    mutationFn: () => api<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => settle(null),
  });
  const updateMe = useUpdateMe();

  return { demo, login, signup, logout, updateMe };
}

/**
 * A change to the signed-in account, as its own mutation. Each caller gets an
 * independent one, so two controls on a page don't share one "Saved." — the
 * settings page once showed the layout switch's success under a profile form
 * that hadn't been saved.
 */
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Partial<{ displayName: string; bio: string; isPublic: boolean; layout: "starter" | "pro" }>,
    ) => api<{ user: SessionUser }>("/me", { method: "PATCH", body }),
    onSuccess: (r) => {
      qc.setQueryData(ME, r.user);
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    },
  });
}
